/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Change data capture (CDC)", description = "How Hightouch detects added, changed, and removed rows between sync runs and sends only the difference.", lastUpdatedDate = new Date(1787184000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Change data capture (CDC)",
        description: "How Hightouch detects added, changed, and removed rows between sync runs and sends only the difference.",
        lastUpdatedDate: "Thu Aug 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "How CDC works",
          "depth": 0,
          "id": "how-cdc-works"
        }, {
          "value": "CDC depends on a unique primary key",
          "depth": 0,
          "id": "cdc-depends-on-a-unique-primary-key"
        }, {
          "value": "CDC by sync mode",
          "depth": 0,
          "id": "cdc-by-sync-mode"
        }, {
          "value": "Reset or reprocess CDC",
          "depth": 0,
          "id": "reset-or-reprocess-cdc"
        }, {
          "value": "When CDC runs",
          "depth": 0,
          "id": "when-cdc-runs"
        }, {
          "value": "Where CDC runs",
          "depth": 0,
          "id": "where-cdc-runs"
        }, {
          "value": "Where diff files are stored",
          "depth": 0,
          "id": "where-diff-files-are-stored"
        }, {
          "value": "Why reverse ETL uses difference-based CDC",
          "depth": 0,
          "id": "why-reverse-etl-uses-difference-based-cdc"
        }, {
          "value": "FAQ",
          "depth": 0,
          "id": "faq"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      em: "em",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      h2: "h2",
      ol: "ol",
      li: "li",
      a: "a",
      img: "img",
      ul: "ul"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Change data capture (CDC) is how Hightouch detects which rows changed between sync runs, so a sync sends only the difference instead of the whole dataset."
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data teams configuring or troubleshooting syncs"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: "A model with a unique primary key."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Without CDC, every run would resend your entire dataset, which means more API calls, slower syncs, and wasted downstream processing. With CDC, a sync sends only what changed since the last run."
      }), "\n", _jsx(_components.h2, {
        id: "how-cdc-works",
        children: "How CDC works"
      }), "\n", _jsxs(_components.p, {
        children: ["Each time a sync runs, Hightouch compares the current query results against a stored snapshot of the previous run, called the ", _jsx(_components.strong, {
          children: "diff file"
        }), ", and sends only the rows that differ:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Query the model against your source."
        }), "\n", _jsxs(_components.li, {
          children: ["Compare the results to the previous run's diff file, matching rows by ", _jsx(_components.a, {
            href: "/models/creating-models#unique-primary-key-requirement",
            children: "primary key"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Classify each row as added, changed, or removed."
        }), "\n", _jsx(_components.li, {
          children: "Send those changes to the destination, along with any rows that failed on the previous run."
        }), "\n", _jsx(_components.li, {
          children: "Save a new diff file for the next comparison."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/cdc-simplified.png",
          alt: "Diagram of Hightouch comparing current query results to the previous diff file"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["CDC compares only mapped model columns. The exception is custom destinations,\nsuch as ", _jsx(_components.a, {
            href: "/destinations/http-request",
            children: "HTTP Request"
          }), ", which consider every\ncolumn."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "cdc-depends-on-a-unique-primary-key",
        children: "CDC depends on a unique primary key"
      }), "\n", _jsxs(_components.p, {
        children: ["The primary key is how CDC identifies a row from one run to the next. If primary keys are missing or duplicated, Hightouch can't reliably tell an added row from a changed one. See ", _jsx(_components.a, {
          href: "/models/creating-models#unique-primary-key-requirement",
          children: "primary key requirements"
        }), "."]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Changing a model's primary key breaks CDC's record identity. If you select a\ndifferent primary key column, reset CDC for every sync that depends on the\nmodel. If you keep the same column but change how its values are calculated,\nrecords may be added or removed in your destination, depending on the sync\nconfiguration."
        })
      }), "\n", _jsx(_components.h2, {
        id: "cdc-by-sync-mode",
        children: "CDC by sync mode"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync mode"
        }), " determines which detected changes become destination operations:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), " — sends only rows whose primary key is new since the previous run."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update"
          }), " and ", _jsx(_components.strong, {
            children: "Upsert"
          }), " — send rows whose mapped values changed."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Archive"
          }), " and ", _jsx(_components.strong, {
            children: "All"
          }), " (sometimes called ", _jsx(_components.strong, {
            children: "Mirror"
          }), ") — don't use CDC. Every row is sent on every run, and these modes don't store requests in the ", _jsx(_components.a, {
            href: "/syncs/debugger",
            children: "live debugger"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For the full mode reference, see ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes-and-change-data-capture",
          children: "sync modes and change data capture"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "reset-or-reprocess-cdc",
        children: "Reset or reprocess CDC"
      }), "\n", _jsx(_components.p, {
        children: "Some changes require Hightouch to rebuild CDC state before the next run behaves as you expect. Reset CDC, full resync, and clear-and-fill are run behaviors, not sync modes."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "When this happens"
            }), _jsx(_components.th, {
              children: "What to do"
            }), _jsx(_components.th, {
              children: "Effect"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "You change a model's primary key"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/syncs/resync-reset-clear#reset-cdc",
                children: "Reset CDC"
              })
            }), _jsx(_components.td, {
              children: "The next run captures a fresh baseline and sends nothing."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "You change field mappings"
            }), _jsx(_components.td, {
              children: "Nothing; it's automatic"
            }), _jsxs(_components.td, {
              children: ["Hightouch reprocesses the ", _jsx(_components.a, {
                href: "/syncs/mapping-data#field-mapping-updates",
                children: "model results"
              }), " on the next run."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "A sync hasn't run in over 30 days"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "/syncs/resync-reset-clear#reset-cdc",
                children: "Reset CDC"
              }), " or ", _jsx(_components.a, {
                href: "/syncs/resync-reset-clear#full-resync",
                children: "full resync"
              })]
            }), _jsx(_components.td, {
              children: "The diff file has expired, so CDC has no baseline to compare against."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "You need to rebuild destination state"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/syncs/resync-reset-clear#full-resync",
                children: "Full resync"
              })
            }), _jsx(_components.td, {
              children: "Processes every current row as if it were newly added."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For the consequences and click paths of each action, see the ", _jsx(_components.a, {
          href: "/syncs/resync-reset-clear#reset-cdc",
          children: "Syncs overview"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "when-cdc-runs",
        children: "When CDC runs"
      }), "\n", _jsxs(_components.p, {
        children: ["CDC happens after Hightouch receives the query results and before it writes to the destination. While the sync status shows ", _jsx(_components.strong, {
          children: "Querying"
        }), ", Hightouch is waiting for query results, computing the diff, or saving the new diff file."]
      }), "\n", _jsx(_components.h2, {
        id: "where-cdc-runs",
        children: "Where CDC runs"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch computes CDC on its own infrastructure, called the basic engine. For supported sources, the ", _jsx(_components.a, {
          href: "/syncs/lightning-sync-engine",
          children: "Lightning Sync Engine"
        }), " computes CDC directly in your warehouse, which speeds up syncs at higher volumes."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "get-started/cdc-diagram.png",
          alt: "Basic engine compared with the Lightning Sync Engine"
        })
      }), "\n", _jsx(_components.h2, {
        id: "where-diff-files-are-stored",
        children: "Where diff files are stored"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch stores the previous run's query results to compute the next diff. By default, diff files live in an encrypted, Hightouch-managed bucket and are retained for 30 days. Some plans let you ", _jsx(_components.a, {
          href: "/security/storage#self-hosted-storage",
          children: "bring your own bucket"
        }), " to keep diffs in your own infrastructure."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/byob.png",
          alt: "Bring your own bucket for diff storage"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If a sync doesn't run for more than 30 days, its diff file expires. The next run needs a ", _jsx(_components.a, {
          href: "/syncs/resync-reset-clear#reset-cdc",
          children: "reset CDC"
        }), " or a ", _jsx(_components.a, {
          href: "/syncs/resync-reset-clear#full-resync",
          children: "full resync"
        }), " to establish a new baseline."]
      }), "\n", _jsx(_components.h2, {
        id: "why-reverse-etl-uses-difference-based-cdc",
        children: "Why reverse ETL uses difference-based CDC"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch compares full before-and-after query results, a method called ", _jsx(_components.strong, {
          children: "difference-based CDC"
        }), ". Warehouses can't produce CDC logs for arbitrary SQL queries or dbt models, so log-based CDC isn't possible when reading from them."]
      }), "\n", _jsx(_components.p, {
        children: "This is the reverse of traditional ETL. OLTP databases such as Postgres and MySQL log incremental changes natively, and ETL tools use those logs when loading data into a warehouse. Because Hightouch sends data out of the warehouse, it diffs query results instead."
      }), "\n", _jsx(_components.h2, {
        id: "faq",
        children: "FAQ"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Does Hightouch keep historical diff files?"
        })
      }), "\n", _jsx(_components.p, {
        children: "No. Hightouch stores only the most recent diff file and compares against it."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "What happens if a sync fails?"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["CDC retries the failed rows on the next run. See ", _jsx(_components.a, {
          href: "/syncs/retries",
          children: "retries"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "What happens if I stop syncing for more than 30 days?"
        })
      }), "\n", _jsx(_components.p, {
        children: "The diff file in managed storage expires. Reset CDC or run a full resync to rebuild the baseline."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "What happens if I change my mappings?"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Changing mappings can trigger a full reprocess on the next run. See ", _jsx(_components.a, {
          href: "/syncs/mapping-data#field-mapping-updates",
          children: "field mapping updates"
        }), "."]
      })]
    });
  }
}
export default MDXContent;
